import { inputActions } from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputKeymap } from 'app/modules/wallforming/components/input-binding/types/inputKeymap';
import { InputPresetId } from 'app/modules/wallforming/components/input-binding/types/inputPresetId';
import { InputProfile } from 'app/modules/wallforming/components/input-binding/types/inputProfile';

const customKeymap: InputKeymap = {
    [InputActionId.GeneralOpenKeyBindingPanel]: ['F1', 'Shift+Minus'],
    [InputActionId.GeneralCancel]: ['Escape'],
    [InputActionId.GeneralOpenProjects]: ['Ctrl+KeyO'],
    [InputActionId.GeneralSave]: ['Ctrl+KeyS'],
    [InputActionId.GeneralToggleViewMode]: ['Shift+KeyV'],
    [InputActionId.GeneralToggleCopilot]: ['Ctrl+Shift+KeyI'],

    [InputActionId.CameraCenter]: ['Mouse1@doubleClick', null],
    [InputActionId.CameraLockToggle]: ['KeyL'],
    [InputActionId.CameraUnlockToggle]: ['KeyU'],
    [InputActionId.CameraRotate]: ['Shift+Mouse1@drag'],
    [InputActionId.CameraTruck]: ['Mouse1@drag'],
    [InputActionId.CameraZoom]: ['Wheel'],

    [InputActionId.PoiNavigationModeToggle]: ['KeyP'],
    [InputActionId.PoiNavigationTemporary]: ['Space'],
    [InputActionId.PoiPrevious]: ['KeyA', 'ArrowLeft'],
    [InputActionId.PoiNext]: ['KeyD', 'ArrowRight'],
    [InputActionId.PoiSwitchType]: ['Tab'],
    [InputActionId.PoiSwitchTypeReversed]: ['Shift+Tab'],
    [InputActionId.PoiToggleCameraRealignment]: ['KeyC'],
    [InputActionId.PoiSelect]: ['Mouse0@click'],
    [InputActionId.PoiSelectAndAlign]: ['Mouse0@doubleClick'],

    [InputActionId.ArticleCopy]: ['Ctrl+KeyC'],
    [InputActionId.ArticleCut]: ['Ctrl+KeyX'],
    [InputActionId.ArticlePaste]: ['Ctrl+KeyV'],
    [InputActionId.ArticleSelectAll]: ['Ctrl+KeyA'],
    [InputActionId.ArticleDelete]: ['Delete'],
    [InputActionId.ArticleTransform]: ['Shift+KeyT'],
    [InputActionId.ArticleRotate]: ['KeyR'],
    [InputActionId.ArticleQuickReplacement]: ['KeyQ'],
    [InputActionId.ArticleQuickReplacementLeft]: ['ArrowLeft', 'KeyA'],
    [InputActionId.ArticleQuickReplacementRight]: ['ArrowRight', 'KeyD'],
    [InputActionId.ArticleQuickReplacementUp]: ['ArrowUp', 'KeyW'],
    [InputActionId.ArticleQuickReplacementDown]: ['ArrowDown', 'KeyS'],
    [InputActionId.ArticleQuickReplacementInwardModifier]: ['Shift'],
    [InputActionId.ArticleGroup]: ['Ctrl+KeyG'],
    [InputActionId.ArticleUngroup]: ['Ctrl+Shift+KeyG'],
    [InputActionId.ArticleShowContextMenu]: ['Mouse2@click'],
    [InputActionId.ArticleCatalogueDrag]: ['Mouse0@drag'],

    [InputActionId.SelectionBoxFullyEnclosed]: ['Mouse0@drag'],
    [InputActionId.SelectionBoxPartiallyEnclosed]: ['Mouse0@drag'],
    [InputActionId.SelectionBoxRecursive]: ['Ctrl+Mouse0@drag'],

    [InputActionId.PlacementExecute]: ['Enter', 'KeyE'],
    [InputActionId.PlacementApplySelectedSolution]: ['Enter', 'KeyE'],
    [InputActionId.PlacementListNavigationUp]: ['KeyW', 'ArrowUp'],
    [InputActionId.PlacementListNavigationDown]: ['KeyS', 'ArrowDown']
};

/**
 * Single-key approximations of Revit (`MV` move, `GP`/`UG` group) and AutoCAD (`M` move, `G` group, `E` erase)
 * command aliases; both use double-click on the wheel to zoom extents. Multi-key sequences are not supported.
 */
const cadKeymap: InputKeymap = {
    ...customKeymap,
    [InputActionId.ArticleTransform]: ['KeyM'],
    [InputActionId.ArticleGroup]: ['KeyG'],
    [InputActionId.ArticleUngroup]: ['Shift+KeyG']
};

export const inputPresets: Record<InputPresetId, InputKeymap> = {
    [InputPresetId.Custom]: customKeymap,
    [InputPresetId.Revit]: cadKeymap,
    [InputPresetId.AutoCad]: {
        ...cadKeymap,
        [InputActionId.ArticleDelete]: ['Delete', 'KeyE'],
        [InputActionId.PlacementExecute]: ['Enter'],
        [InputActionId.PlacementApplySelectedSolution]: ['Enter']
    },
    [InputPresetId.Empty]: {}
};

export const DEFAULT_INPUT_PROFILE_ID = InputPresetId.Custom;

export const builtInInputProfiles: readonly InputProfile[] = [
    { id: InputPresetId.Custom, name: 'Custom', basedOn: InputPresetId.Custom, overrides: {}, builtIn: true },
    {
        id: InputPresetId.Revit,
        name: 'Revit',
        basedOn: InputPresetId.Revit,
        overrides: {},
        builtIn: true,
        readOnly: true
    },
    {
        id: InputPresetId.AutoCad,
        name: 'AutoCAD',
        basedOn: InputPresetId.AutoCad,
        overrides: {},
        builtIn: true,
        readOnly: true
    }
];

export const isInputActionId = (value: string): value is InputActionId => value in inputActions;
export const isInputPresetId = (value: unknown): value is InputPresetId =>
    Object.values(InputPresetId).includes(value as InputPresetId);
